import client from './client';
import {
  InventoryAlert,
  InventoryCostLayer,
  InventoryItem,
  InventoryTransaction,
  RestockWaitingGroup,
} from '../types/inventory';

function asArray<T>(raw: any, key: string): T[] {
  return (
    (Array.isArray(raw?.[key]) && raw[key]) ||
    (Array.isArray(raw?.data) && raw.data) ||
    (Array.isArray(raw) && raw) ||
    []
  );
}

export async function getInventory(): Promise<InventoryItem[]> {
  const res = await client.get('/api/admin/inventory');
  return asArray<InventoryItem>(res.data, 'inventory');
}

export async function getAlerts(): Promise<InventoryAlert[]> {
  const res = await client.get('/api/admin/inventory/alerts');
  return asArray<InventoryAlert>(res.data, 'alerts');
}

// Records a priced batch: adds the stock and opens the FIFO cost layer its ml
// will be costed from. cost_per_ml is the price of THIS batch, not the oil's
// running average — restocking at a new price leaves older stock costed at what
// it actually cost.
export async function restockOil(
  oilName: string,
  quantityMl: number,
  costPerMl: number,
  notes: string
): Promise<void> {
  await client.post(`/api/admin/inventory/${encodeURIComponent(oilName)}/restock`, {
    quantity_ml: quantityMl,
    cost_per_ml: costPerMl,
    notes,
  });
}

export async function getCostLayers(oilName: string): Promise<InventoryCostLayer[]> {
  const res = await client.get(`/api/admin/inventory/${encodeURIComponent(oilName)}/layers`);
  return asArray<InventoryCostLayer>(res.data, 'layers');
}

export async function updateOilCost(oilName: string, unitCostPerMl: number): Promise<void> {
  await client.put(`/api/admin/inventory/${encodeURIComponent(oilName)}/cost`, {
    unit_cost_per_ml: unitCostPerMl,
  });
}

// The two per-oil reorder knobs:
//   reorder_level_ml — alert threshold; stock at or below it reads as low.
//   reorder_qty_ml   — suggested restock size, advisory only.
// Both optional — only what is passed is written. Returns the updated row so the
// table can be patched in place, plus a warning when the new threshold is at or
// above current stock (allowed: that's a pre-emptive alert, not a mistake).
export async function updateReorderSettings(
  oilName: string,
  settings: {
    reorder_level_ml?: number;
    reorder_qty_ml?: number;
    /** null clears the shelf number; omit the key to leave it untouched. */
    manufacturer_code?: number | null;
  }
): Promise<{ item: InventoryItem; warning?: string }> {
  const res = await client.patch(
    `/api/admin/inventory/${encodeURIComponent(oilName)}/reorder-settings`,
    settings
  );
  return { item: res.data?.item, warning: res.data?.warning };
}

// Set or clear the number the manufacturer wrote on the bottle. Its own request
// so a shelf can be renumbered without touching stock thresholds.
//
// Rejects with 409 when another oil already holds the number — the server's
// message names that oil, so surface it as-is rather than inventing one.
export async function setManufacturerCode(
  oilName: string,
  code: number | null
): Promise<InventoryItem> {
  const res = await client.patch(`/api/admin/inventory/${encodeURIComponent(oilName)}/code`, {
    manufacturer_code: code,
  });
  return res.data?.item;
}

export async function resolveAlert(id: string): Promise<void> {
  await client.put(`/api/admin/inventory/alerts/${id}/resolve`);
}

// Everyone still waiting on a restock, grouped by the item they're waiting for.
// Feeds the "3 waiting" badge on the stock table — restocking an oil with a
// queue behind it is a different decision from restocking one without.
export async function getRestockWaiting(): Promise<RestockWaitingGroup[]> {
  const res = await client.get('/api/admin/restock-notifications');
  return asArray<RestockWaitingGroup>(res.data, 'groups');
}

export async function getTransactions(limit = 50): Promise<InventoryTransaction[]> {
  const res = await client.get('/api/admin/inventory/transactions', { params: { limit } });
  return asArray<InventoryTransaction>(res.data, 'transactions');
}
